import {
    html,
    classMap,
    styleMap,
    createRef,
    ref,
    when,
    SuperElement,
} from './lit-base.js'
import axios from 'axios'
import {
    obtenerPosicionRelativa,
    buscarPorId,
    sinDiacriticos,
} from '../util.js'

export default class BuscadorAreasWebs extends SuperElement {
    static properties = {
        avisoresultadoslimitados: {
            type: String,
        },
        disabled: {
            type: Boolean,
        },
        idioma: {
            type: String,
        },
        maxwidth: {
            type: Number,
        },
        minlength: {
            type: Number,
        },
        multiple: {
            type: Boolean,
        },
        name: {
            type: String,
        },
        placeholder: {
            type: String,
        },
        placeholderbuscador: {
            type: String,
        },
        textoerror: {
            type: String,
        },
        textosinresultados: {
            type: String,
        },
        type: {
            type: String,
        },
        url: {
            type: String,
        },
        value: {
            type: String,
        },
        _areas: {
            type: Array,
        },
        _buscando: {
            type: Boolean,
        },
        _busqueda: {
            type: String,
        },
        _desplegableLeft: {
            type: String,
        },
        _desplegableTop: {
            type: String,
        },
        _posicionandoDesplegableTrasBusqueda: {
            type: Boolean,
        },
        _title: {
            type: String,
        },
        _value: {
            type: Array,
        },
        _valueName: {
            type: String,
        },
        _viendoDesplegable: {
            type: Boolean,
        },
        _viendoDesplegableArriba: {
            type: Boolean,
        },
        _viendoInputBusqueda: {
            type: Boolean,
        },
    }

    constructor() {
        super()

        this.avisoresultadoslimitados = 'Mostrando resultados más relevantes, introduce una búsqueda más detallada'
        this.disabled = false
        this.idioma = ''
        this.maxwidth = ''
        this.minlength = 0
        this.multiple = false
        this.name = ''
        this.placeholder = 'Buscar áreas'
        this.placeholderbuscador = 'Escribe...'
        this.textoerror = 'Ha habido un error al realizar la búsqueda'
        this.textosinresultados = 'No se han encontrado resultados'
        this.url = ''
        this.type = 'WEB-COMPONENT'

        this._areas = []
        this._buscando = false
        this._busqueda = ''
        this._desplegableLeft = null
        this._desplegableTop = null
        this._viendoResultadosLimitados = false
        this._LIMITE_RESULTADOS_INICIALES = 6
        this._posicionandoDesplegableTrasBusqueda = false
        this._title = ''
        this._value = []
        this._valueName = ''
        this._viendoDesplegable = false
        this._viendoDesplegableArriba = false
        this._viendoInputBusqueda = false

        this._cacheBusquedas = new Map()
        this._idTimeoutBusqueda = null
        this._mensajeErrorBusqueda = ''
        this._MS_TIMEOUT_BUSQUEDA = 700
        this._oldValue = ''

        this._inputBusquedaRef = createRef()
        this._inputValorRef = createRef()
        this._desplegableRef = createRef()

        this._busquedaPrevia = ''
        this._cargandoValueInicial = false
        this._componenteIniciado = false
    }

    createRenderRoot() {
        return this
    }

    connectedCallback() {
        super.connectedCallback()
        this._componenteIniciado = true
    }

    disconnectedCallback() {
        document.body.removeEventListener('click', this._cerrarClickFuera, true)
        document.body.removeEventListener(
            'scroll',
            this._cerrarScrollFuera,
            true
        )
        document.body.removeEventListener('keyup', this._cerrarEsc)
    }

    get _inputBusqueda() {
        return this._inputBusquedaRef.value
    }

    get _inputValor() {
        return this._inputValorRef.value
    }

    get _desplegable() {
        return this._desplegableRef.value
    }

    _recogerIdsHijos(objeto) {
        let ids = []

        if (objeto.hijos) {
            for (let hijo of objeto.hijos) {
                ids.push(hijo.id)
                ids = ids.concat(this._recogerIdsHijos(hijo))
            }
        }

        return ids
    }

    _cargarIdsHijos(value) {
        let ids = value.split(',').filter(Boolean)
        let idsHijos = []

        for (let id of ids) {
            let objeto = buscarPorId(this._areas, id)
            if (objeto.hijos) {
                idsHijos = idsHijos.concat(this._recogerIdsHijos(objeto))
            }
        }

        return ids.concat(idsHijos)
    }

    // Función que simplifica una estructura de árbol, conservando solo los IDs de los nodos padre
    // si todos sus hijos están presentes en el array de IDs proporcionado
    _filtrarValueMultiple(padres, ids) {
        let resultado = [];

        for (let padre of padres) {
            if (ids.includes(padre.id)) {
                resultado.push(padre.id);
            } else {
                resultado = resultado.concat(this._filtrarValueMultiple(padre.hijos, ids));
            }
        }

        return [...new Set(resultado.filter(Boolean))].join(',');
    }

    get value() {
        if (!this.multiple) {
            return this._value[0]
        }

        return this._filtrarValueMultiple(this._areas, this._value)
    }

    async _setValueAsync(newValue) {
        if (!this._componenteIniciado && this._areas.length === 0) {
            await this._lanzarBusquedaSetValue()
        }

        this._value = !this.multiple
            ? [newValue]
            : this._cargarIdsHijos(newValue)

        this._responderCambioValue()
    }

    set value(newValue) {
        this._setValueAsync(newValue)
    }

    _cerrarAcordeones() {
        this._desplegable.querySelectorAll('.acordeon').forEach((acordeon) => {
            acordeon.open = false
        })
    }

    _reiniciarValueTrasNuevaBusqueda(busqueda) {
        if (busqueda !== this._busquedaPrevia) {
            this._busquedaPrevia = this._busqueda
            this.value = ''
            this._reflejarValueEnCheckbox()
            this._cerrarAcordeones()
        }
    }

    _sanearTexto(texto) {
        return sinDiacriticos(texto).toLowerCase().trim()
    }

    _filtrarAreasSinPropiedades(areas) {
        return areas
            .filter(item => item.visibleDesplegable !== 0)
            .map(item => {
                if (item.hijos) {
                    item.hijos = this._filtrarAreasSinPropiedades(item.hijos);
                }
                return item;
            })
    }

    _filtrarResultadosIniciales(data, busqueda) {
        return data.filter((item) => {
            const hayCoincidencia = this._sanearTexto(item.nombre).includes(
                this._sanearTexto(busqueda)
            )

            if (hayCoincidencia) {
                return true
            }

            if (item.hijos) {
                const hijosFiltrados = this._filtrarResultados(
                    item.hijos,
                    busqueda
                )

                if (hijosFiltrados.length > 0) {
                    item.hijos = hijosFiltrados
                    return true
                }
            }

            return false
        })
    }

    _filtrarResultadosConBusqueda(data, busqueda) {
        return data.reduce((result, item) => {
            let hijosFiltrados = []

            if (item.hijos) {
                hijosFiltrados = this._filtrarResultados(item.hijos, busqueda)
            }

            const hayCoincidencia = this._sanearTexto(item.nombre).includes(
                this._sanearTexto(busqueda)
            )

            if (hayCoincidencia) {
                const itemCopy = { ...item }
                result.push(itemCopy)
            }

            if (hijosFiltrados.length > 0) {
                result = result.concat(hijosFiltrados)
            }

            return result
        }, [])
    }

    _filtrarResultados(data, busqueda) {
        return this._busqueda.trim() === ''
            ? this._filtrarResultadosIniciales(data, busqueda)
            : this._filtrarResultadosConBusqueda(data, busqueda)
    }

    async _getAreas(busqueda) {
        let resultados = []

        if (this._cacheBusquedas.has(busqueda)) {
            resultados = this._cacheBusquedas.get(busqueda)
        } else {
            const { data } = await axios.get(this.url)
            const areas = busqueda.trim() === '' ? this._filtrarAreasSinPropiedades(data) : data
            resultados = this._filtrarResultados(areas, busqueda)

            this._cacheBusquedas.set(busqueda, resultados)
        }

        const deboLimitarResultados = busqueda === ''
            && this._LIMITE_RESULTADOS_INICIALES !== null
            && resultados.length > this._LIMITE_RESULTADOS_INICIALES

        if (deboLimitarResultados) {
            this._viendoResultadosLimitados = true
            resultados = resultados.slice(0, this._LIMITE_RESULTADOS_INICIALES)
        } else {
            this._viendoResultadosLimitados = false
        }

        return resultados
    }

    borrarCacheBusquedas() {
        this._cacheBusquedas.clear()
    }

    _responderCambioValue() {
        this._oldValue = this._value
        this._valueName = this._getValueName()
        this._title = this._getTitle()
    }

    _getNombreOpcionUnica() {
        const idArea = this._value[0]
        const area = buscarPorId(this._areas, idArea)
        return area?.nombre ?? ''
    }

    _getNombreOpcionMultiple() {
        const valueConIdsSinRepetir = [...new Set(this._value)]

        const valueName = valueConIdsSinRepetir
            .map((id) => {
                const area = buscarPorId(this._areas, id)
                return area.nombre ?? ''
            })
            .join(', ')

        return valueName
    }

    _getValueName() {
        return this.multiple
            ? this._getNombreOpcionMultiple()
            : this._getNombreOpcionUnica()
    }

    _getTitle() {
        return this._getValueName().split(', ').join('\n')
    }

    _dispararEventoChange() {
        this.dispatchEvent(new Event('change', { bubbles: true }))
    }

    _cerrarDesdeFuera = (event) => {
        if (!this.contains(event.target)) {
            this._cerrar()
        }
    }

    _cerrarConEscape = (event) => {
        if (event.key.toLowerCase() === 'escape') {
            this._cerrar()

            event.stopPropagation()
        }
    }

    _agregarEventListeners() {
        document.body.addEventListener('click', this._cerrarDesdeFuera, true)
        document.body.addEventListener('scroll', this._cerrarDesdeFuera, true)
        document.body.addEventListener('keyup', this._cerrarConEscape)
    }

    _eliminarEventListeners() {
        document.body.removeEventListener('click', this._cerrarDesdeFuera, true)
        document.body.removeEventListener(
            'scroll',
            this._cerrarDesdeFuera,
            true
        )
        document.body.removeEventListener('keyup', this._cerrarConEscape)
    }

    _abrirCategoriasSeleccionadas() {
        const categorias = this._desplegable.querySelectorAll('.acordeon')

        categorias.forEach((categoria) => {
            const tieneHijosSeleccionados = Boolean(
                categoria.querySelector('.contenedor-hijos .js-opcion:checked')
            )

            if (tieneHijosSeleccionados) {
                categoria.open = true
            }
        })
    }

    _posicionarDesplegable() {
        const { top, left, hayEspacioDebajo } = obtenerPosicionRelativa(
            this._desplegable,
            this._inputBusqueda
        )

        this._desplegableLeft = `${left}px`
        this._desplegableTop = `${top}px`
        this._viendoDesplegableArriba = !hayEspacioDebajo
    }

    _reflejarValueEnCheckbox() {
        this._desplegable.querySelectorAll('.js-opcion').forEach((opcion) => {
            const id = opcion.dataset.id
            opcion.checked = this._value.includes(id)
        })
    }

    _actualizarEstadoPadres() {
        const padres = this._desplegable.querySelectorAll(
            '.js-opcion-con-hijos'
        )

        padres.forEach((padre) => {
            const tieneHijos = padre.classList.contains('js-opcion-con-hijos')

            if (tieneHijos) {
                const acordeon = padre.closest('.acordeon')
                const contenedorHijos =
                    acordeon.querySelector('.contenedor-hijos')
                const hijos = contenedorHijos.querySelectorAll('.js-opcion')
                const estanTodosLosHijosMarcados = [...hijos].every(
                    (hijo) => hijo.checked
                )
                padre.checked = estanTodosLosHijosMarcados
            }
        })
    }

    _actualizarCheckboxesConMismaId(id, esCheckboxActivo) {
        const opciones = this._desplegable.querySelectorAll('.js-opcion')

        const actualizarOpcion = (opcion) => {
            if (opcion.dataset.id === id) {
                opcion.checked = esCheckboxActivo
            }
        }

        opciones.forEach(actualizarOpcion)
    }

    _actualizarEstadoCheckboxes(checkbox) {
        const tieneHijos = checkbox.classList.contains('js-opcion-con-hijos')

        if (tieneHijos) {
            const padreMarcado = checkbox.checked
            const acordeon = checkbox.closest('.acordeon')

            if (!acordeon.open) {
                acordeon.open = true
            }

            const contenedorHijos = acordeon.querySelector('.contenedor-hijos')
            const hijos = contenedorHijos.querySelectorAll('.js-opcion')

            hijos.forEach((hijo) => (hijo.checked = padreMarcado))
        }

        const { id } = checkbox.dataset
        const esCheckboxActivo = checkbox.checked
        this._actualizarCheckboxesConMismaId(id, esCheckboxActivo);

        this._actualizarEstadoPadres()
    }

    async _abrirDesplegable() {
        this._viendoDesplegable = true

        this._posicionandoDesplegableTrasBusqueda = true
        await this.finCargaDOM()
        this._posicionarDesplegable()
        this._posicionandoDesplegableTrasBusqueda = false

        this._reflejarValueEnCheckbox()
        this._actualizarEstadoPadres()
        this._abrirCategoriasSeleccionadas()
    }

    async _responderToggleAcordeon() {
        await this.finCargaDOM()
        this._posicionarDesplegable()
    }

    _enfocarInputBusqueda() {
        setTimeout(() => this._inputBusqueda.focus(), 200)
    }

    _habilitarBusqueda() {
        if (this._viendoInputBusqueda || this.disabled) return

        this._viendoInputBusqueda = true
        this._enfocarInputBusqueda()

        this._agregarEventListeners()
    }

    async _buscar(busqueda) {
        this._buscando = true
        this._busqueda = busqueda
        this._areas = []
        this._mensajeErrorBusqueda = ''
        this._viendoDesplegable = false

        try {
            this._areas = await this._getAreas(busqueda)
        } catch (error) {
            this._mensajeErrorBusqueda = this.textoerror
        } finally {
            this._buscando = false
            await this._abrirDesplegable()
            this._reiniciarValueTrasNuevaBusqueda(busqueda)
        }
    }

    _lanzarBusqueda(busqueda) {
        this._idTimeoutBusqueda = setTimeout(
            () => this._buscar(busqueda),
            this._MS_TIMEOUT_BUSQUEDA
        )
    }

    async _lanzarBusquedaSetValue() {
        this._cargandoValueInicial = true
        await this._buscar('')
        this._viendoDesplegable = false
        this._cargandoValueInicial = false
    }

    async _lanzarBusquedaEnFocus() {
        await this._buscar(this._busqueda)
    }

    _responderBusqueda(event) {
        event.stopPropagation()

        clearTimeout(this._idTimeoutBusqueda)

        const busqueda = event.target.value.trim()

        const deboLanzarBusqueda =
            busqueda === '' || busqueda.length >= this.minlength

        if (deboLanzarBusqueda) {
            this._forzarBusqueda(busqueda)
        }
    }

    _forzarBusqueda(busqueda) {
        clearTimeout(this._idTimeoutBusqueda)
        this._lanzarBusqueda(busqueda)
    }

    async _responderFocus() {
        this._habilitarBusqueda()
        await this._lanzarBusquedaEnFocus()
    }

    _cerrar() {
        if (!this._viendoInputBusqueda) return

        this._viendoInputBusqueda = false
        this._viendoDesplegable = false
        this._eliminarEventListeners()

        document.activeElement?.blur()

        if (this._value !== this._oldValue) {
            this._responderCambioValue()
            this._dispararEventoChange()
        }
    }

    _obtenerNuevoValueTrasToggleCheckbox(checkbox) {
        const idOpcion = checkbox.dataset.id

        if (!this.multiple) {
            return [idOpcion]
        }

        const value = Array.from(
            this._desplegable.querySelectorAll('.js-opcion:checked'),
            (opcion) => opcion.dataset.id
        )

        const valueSinIdsRepetidos = [...new Set(value)]
        return valueSinIdsRepetidos
    }

    _responderToggleCheckbox(event) {
        event.stopPropagation()

        const checkbox = event.target

        if (this.multiple) {
            this._actualizarEstadoCheckboxes(checkbox)
        }

        const nuevoValue = this._obtenerNuevoValueTrasToggleCheckbox(checkbox)

        this._value = nuevoValue

        if (!this.multiple) {
            this._cerrar()
        }
    }

    _crearInputValor() {
        return html`
            <input
                ${ref(this._inputValorRef)}
                type="text"
                class="buscador-areas__input-valor"
                placeholder="${this.placeholder}"
                .value=${this._valueName}
                @focus=${this._responderFocus}
                readonly
            />
        `
    }

    _crearInputBusqueda() {
        const className = classMap({
            'buscador-areas__input-busqueda': true,
            'buscador-areas__input-busqueda--visible':
                this._viendoInputBusqueda,
        })

        return html`
            <input
                ${ref(this._inputBusquedaRef)}
                type="text"
                class="${className}"
                placeholder="${this.placeholderbuscador}"
                ?disabled=${this.disabled}
                .value="${this._busqueda}"
                @input=${this._responderBusqueda}
            />
        `
    }

    _crearToggle() {
        const onClick = this._viendoInputBusqueda
            ? this._cerrar
            : this._responderFocus

        return html`
            <button class="buscador-areas__toggle" @click=${onClick}>
                <i class="buscador-areas__icono-flecha ic icArrowDown"></i>
            </button>
        `
    }

    _crearNombreOpcion(nombre) {
        if (this._busqueda === '') {
            return html`<span class="opcion__nombre">${nombre}</span>`
        }

        const nombreSaneado = this._sanearTexto(nombre)
        const busquedaSaneada = this._sanearTexto(this._busqueda)

        const inicio = nombreSaneado.indexOf(busquedaSaneada)

        if (inicio === -1) {
            return html`<span class="opcion__nombre">${nombre}</span>`
        }

        const fin = inicio + busquedaSaneada.length
        const antes = nombre.substring(0, inicio)
        const coincidencia = nombre.substring(inicio, fin)
        const despues = nombre.substring(fin)

        const nombreFinal = html`${antes}<mark>${coincidencia}</mark>${despues}`

        return html`<span class="opcion__nombre">${nombreFinal}</span>`
    }

    _crearNombrePadre(nombrePadre) {
        return html`
            <span class="opcion__nombre-padre">(${nombrePadre})</span>
        `
    }

    _crearOpcionSimple(datos) {
        const { id, nombre, nombrePadre } = datos
        const tipo = this.idioma
            ? datos[`tipo_idioma_${this.idioma}`]
            : datos.tipo

        const tieneHijos =
            this._areas.find((x) => x.id === id)?.hijos.length > 0

        const className = classMap({
            'js-opcion': true,
            'js-opcion-con-hijos': tieneHijos,
            opcion__checkbox: true,
            'opcion__checkbox--oculto': !this.multiple,
        })

        return html`
            <label class="opcion">
                <input
                    class="${className}"
                    type="checkbox"
                    data-id="${id}"
                    data-nombre="${nombre}"
                    @change=${this._responderToggleCheckbox}
                />
                <div class="opcion__contenedor-info" title="${nombre}">
                    <span class="opcion__contenedor-nombre">
                        ${this._crearNombreOpcion(nombre)}
                        ${when(nombrePadre, () =>
                            this._crearNombrePadre(nombrePadre)
                        )}
                    </span>

                    <span class="opcion__tipo">${tipo}</span>
                </div>
            </label>
        `
    }

    _crearSlotContenidoExtra() {
        return html`
            <slot
                name="contenido-extra"
                class="buscador-areas__contenido-extra"
            ></slot>
        `
    }

    _crearErrorBusqueda() {
        return html`
            <p class="mensaje-error-busqueda">${this._mensajeErrorBusqueda}</p>
        `
    }

    _crearSinCoincidencias() {
        return html`
            <slot name="sin-coincidencias">
                <div class="sin-coincidencias">
                    <p class="mensaje-sin-coincidencias">
                        ${this.textosinresultados}
                    </p>
                </div>
            </slot>
        `
    }

    _crearOpcionesHijos(hijos) {
        return html`
            <div class="contenedor-hijos">
                ${hijos.map((datosArea) => this._crearContenidoArea(datosArea))}
            </div>
        `
    }

    _crearOpcionConHijos(datosArea) {
        return html`
            <details class="acordeon" @toggle=${this._responderToggleAcordeon}>
                <summary class="acordeon__cabecera">
                    <i class="acordeon__icono ic icMas"></i>
                    <i class="acordeon__icono ic icMenos"></i>
                    ${this._crearOpcionSimple(datosArea)}
                </summary>

                ${this._crearOpcionesHijos(datosArea.hijos)}
            </details>
        `
    }

    _crearContenidoArea(datosArea) {
        const tieneHijos = datosArea.hijos && datosArea.hijos.length > 0

        return tieneHijos
            ? this._crearOpcionConHijos(datosArea)
            : this._crearOpcionSimple(datosArea)
    }

    _crearAreas() {
        return html`
            ${this._areas.map((datosArea) =>
                this._crearContenidoArea(datosArea)
            )}
        `
    }

    _crearRespuestaBusqueda() {
        if (this._mensajeErrorBusqueda !== '') {
            return this._crearErrorBusqueda()
        }

        if (this._areas.length === 0) {
            return this._crearSinCoincidencias()
        }

        return this._crearAreas()
    }

    _crearAvisoResultadosLimitados() {
        return html`
            <div class="aviso-resultados-limitados">
                <p class="aviso-resultados-limitados__texto">
                    ${this.avisoresultadoslimitados}
                </p>
            </div>
        `
    }

    _crearContenidoDesplegable() {
        return html`
            <div class="buscador-areas__contenido-desplegable">
                ${when(this._viendoResultadosLimitados, () => 
                    this._crearAvisoResultadosLimitados()
                )}
                
                <div class="buscador-areas__contenedor-listado">
                    ${this._crearRespuestaBusqueda()}
                </div>

                ${when(this._areas.length > 0, () =>
                    this._crearSlotContenidoExtra()
                )}
            </div>
        `
    }

    _crearDesplegable() {
        const className = classMap({
            'buscador-areas__desplegable': true,
            'buscador-areas__desplegable--posicionando-tras-busqueda':
                this._posicionandoDesplegableTrasBusqueda,
            'buscador-areas__desplegable--abierto': this._viendoDesplegable,
            'buscador-areas__desplegable--arriba':
                this._viendoDesplegableArriba,
        })

        const style = styleMap({
            left: this._desplegableLeft,
            top: this._desplegableTop,
            maxWidth: this.maxwidth,
            display: this._cargandoValueInicial ? 'none' : '',
        })

        return html`
            <div
                ${ref(this._desplegableRef)}
                class="${className}"
                style="${style}"
            >
                ${when(this._viendoDesplegable, () =>
                    this._crearContenidoDesplegable()
                )}
            </div>
        `
    }

    render() {
        const className = classMap({
            'buscador-areas': true,
            'buscador-areas--buscando': this._buscando,
        })

        return html`
            <div class="${className}" .title="${this._title}">
                ${this._crearInputValor()} 
                ${this._crearInputBusqueda()}
                ${this._crearToggle()}
                ${this._crearDesplegable()}
            </div>
        `
    }
}
